import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { saveAs } from 'file-saver';
import { vueData } from '@/VueData';
import { AlertType } from '@/interfaces/IAlert';

export interface IDeliverInvoicePdfInput {
  blob: Blob;
  filename: string;
  /**
   * Opens the in-app preview modal. Injected as a callback because plain
   * utils cannot call composables — the caller owns the modal.
   */
  openPreview: (blob: Blob, filename: string) => void;
}

/**
 * Detects user-cancelled share actions (Web Share API and Capacitor Share
 * plugin) so callers can treat them as silent no-ops instead of errors.
 */
export function isShareCancelledError(e: unknown): boolean {
  if (e && typeof e === 'object' && 'name' in e) {
    const name = String((e as { name?: unknown }).name ?? '');
    // Web Share API cancellation
    if (name === 'AbortError') return true;
  }
  const msg = e instanceof Error ? e.message : String(e);
  // Capacitor Share plugin cancellation (Android)
  return msg.toLowerCase().includes('share canceled');
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(reader.error ?? new Error('No se pudo leer el PDF'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      // Strip the "data:application/pdf;base64," prefix
      const commaIdx = result.indexOf(',');
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Delivers a generated invoice PDF to the user, per platform:
 * - Capacitor native: temp file + native share sheet
 * - Web with inline PDF support: in-app preview modal (with download action)
 * - Web without inline PDF support: direct download
 */
/**
 * True when the native share delivery path can work: legacy shells (old
 * APKs without the Share/Filesystem modules) must fall back to web behavior
 * — callers should also hide share-based actions there, since an Android
 * WebView cannot handle blob downloads either.
 */
export function canShareNativePdf(): boolean {
  return (
    Capacitor.isNativePlatform() &&
    Capacitor.isPluginAvailable('Share') &&
    Capacitor.isPluginAvailable('Filesystem')
  );
}

export async function deliverInvoicePdf(
  input: IDeliverInvoicePdfInput
): Promise<void> {
  const { blob, filename, openPreview } = input;

  if (canShareNativePdf()) {
    try {
      const can = await Share.canShare();
      if (!can.value) {
        vueData.addAlert(
          'Compartir no está disponible en este dispositivo',
          AlertType.Info
        );
        saveAs(blob, filename);
        return;
      }

      const base64 = await blobToBase64(blob);
      const result = await Filesystem.writeFile({
        path: `invoices/${filename}`,
        directory: Directory.Temporary,
        data: base64,
        recursive: true,
      });

      await Share.share({
        title: filename,
        files: [result.uri],
        dialogTitle: 'Compartir factura',
      });
      return;
    } catch (e) {
      if (isShareCancelledError(e)) return;
      throw e;
    }
  }

  if (typeof navigator !== 'undefined' && navigator.pdfViewerEnabled === true) {
    openPreview(blob, filename);
    return;
  }

  saveAs(blob, filename);
}
